import slugify from '@sindresorhus/slugify';
import algoliasearch from 'algoliasearch';
import algoliasearchHelper from 'algoliasearch-helper';
import InfiniteScroll from 'infinite-scroll';
import debounce from 'lodash/debounce';

const ALGOLIA_APP_ID = process.env.ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_KEY = process.env.ALGOLIA_SEARCH_KEY;
const ALGOLIA_INDEX = process.env.ALGOLIA_INDEX;

const {KitModule, registerModule} = window;

const TITLE_MAP = {
  page: 'Page',
  podcastEpisode: 'Podcasts',
  newsroom: 'Newsroom',
  video: 'Video',
  videos: 'Videos',
  research: 'Research',
  'case-studies': 'Case Studies',
  perspectives: 'Perspectives',
  type: 'Content ',
  // DEPRECATED
  contentType: 'Content Type',
  whitepaper: 'Whitepaper',
  postResearch: 'Insights',
  postBlog: 'Galaxy Perspectives',
};

/**
 * get human readable title for string id
 * @param {String} id
 * @return {String}
 */
function getTitle(id) {
  return TITLE_MAP[id] || id;
}

// ---------------------- SearchPage ---------------------- //

class SearchPage extends KitModule {
  constructor($element) {
    super($element);
    // elements
    this.$input = this.$element.querySelector('.search-field__input');
    this.$input.addEventListener('keyup', this);
    this.$input.addEventListener('input', debounce(this.onInput.bind(this), 600));

    // properties
    const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
    this.helper = algoliasearchHelper(client, ALGOLIA_INDEX, {
      facets: ['type'],
      clickAnalytics: true,
    });
    this.currentHitOffset = 0;

    // use infiniteScroll only for scrollThreshold event
    this.infiniteScroll = new InfiniteScroll(this.$main, {
      path: '{{#}}',
      history: false,
      loadOnScroll: false,
    });

    this.infiniteScroll.on('scrollThreshold', this.onScrollThreshold.bind(this));

    this.helper.on('search', this.onHelperSearch.bind(this));
    this.helper.on('result', this.onHelperResult.bind(this));
    this.helper.on('error', this.onHelperError.bind(this));

    this.$sidebar.addEventListener('click', this.onSidebarClick.bind(this));
    this.$main.addEventListener('click', this.onResultClick.bind(this));

    this.initSearch();
  }

  // ----- event methods ----- //

  onkeyup(event) {
    if (event.key === 'Enter' && this.$input.value) {
      this.performSearch(this.$input.value);
    }
  }

  onInput() {
    const query = this.$input.value.trim();
    if (query.length >= 3) {
      this.performSearch(query);
    }
  }

  performSearch(query) {
    this.clearHits();
    this.clearFacets();
    this.setFacet(null, null);
    this.currentHitOffset = 0;
    this.helper.setQuery(query).setPage(0).search();
  }

  onHelperSearch() {
    this.$element.classList.add('is-loading');
    this.isLoading = true;
    this.$status.textContent = 'Loading...';
  }

  onHelperResult(event) {
    this.$element.classList.remove('is-loading');
    this.isLoading = false;
    this.page = event.results.page;
    this.nbPages = event.results.nbPages;

    if (event.results.page === 0) {
      this.currentHitOffset = 0;
    }

    this.renderStatus(event.results);
    this.renderHits(event.results);
    this.renderFacets(event.results);
    this.renderHistory(event.results);
    this.trackViewEvent(event.results.hits);

    this.currentHitOffset += event.results.hits.length;
  }

  onHelperError(error) {
    // eslint-disable-next-line no-console
    console.error('Search error', error);
  }

  onScrollThreshold() {
    const hasMorePages = this.page < this.nbPages - 1;
    if (!this.isLoading && hasMorePages) {
      this.helper.setPage(this.page + 1).search();
    }
  }

  onSidebarClick(event) {
    const $button = event.target.closest('.search-facet__button');
    if (!$button) return;

    if (this.$selectedFacetButton) this.$selectedFacetButton.classList.remove('is-selected');


    const key = $button.getAttribute('data-facet-key');

    if ($button === this.$selectedFacetButton) {
      // same button clicked, reset facet
      this.setFacet(key, null);
    } else {
      const value = $button.getAttribute('data-facet-value');
      this.setFacet(key, value);
      this.$selectedFacetButton = $button;
      this.$selectedFacetButton.classList.add('is-selected');
    }

    this.clearHits();
    this.currentHitOffset = 0;
    this.helper.setPage(0).search();
  }

  // ----- render methods ----- //

  renderStatus(results) {
    let status;
    if (results.nbHits === 0) {
      status = 'No results found';
    } else if (results.nbHits === 1) {
      status = '1 result found';
    } else {
      status = `${results.nbHits} results found`;
    }
    const facetMessage = this.facetValue ? `within ${getTitle(this.facetValue)}` : '';
    this.$status.textContent = `${status} for “${results.query}” ${facetMessage}`;
  }

  renderHits(results) {
    // reset hits if first page
    if (results.page === 0) this.clearHits();

    const offset = this.currentHitOffset;
    const queryID = results.queryID;
    this.$main.innerHTML += results.hits.map((hit, index) => {
      return getHitHtml(hit, offset + index + 1, queryID);
    }).join('\n');
  }

  renderFacets(results) {
    if (this.facetValue) return; // do not re-render on faceted search

    const html = results.facets.map((facet) => {
      const itemsHtml = Object.keys(facet.data)
        .map((value) => {
          const count = facet.data[value];
          return `
            <li class="search-facet__item">
              <button
                class="search-facet__button btn--light"
                data-facet-key="${facet.name}"
                data-facet-value="${value}"
              >
                ${getTitle(value)} ( ${count} )
              </button
            </li>
          `;
        });

      return `<ul class="search-facet__list">
        ${itemsHtml.join('')}
      </ul>`;
    });

    this.$sidebar.innerHTML = html.join('');
  }

  renderHistory(results) {
    // compose url with params
    const params = new URLSearchParams({
      q: results.query,
    });

    const {facetsRefinements} = results._state;
    Object.keys(facetsRefinements).forEach((key) => {
      const value = facetsRefinements[key].join(',');
      params.set(key, value);
    });

    const url = window.location.pathname + '?' + params.toString();
    window.history.replaceState(null, document.title, url);
  }

  // ----- utility methods ----- //

  initSearch() {
    // initialize with q param from browser URL
    const params = new URLSearchParams(window.location.search);
    let query;
    params.forEach((value, key) => {
      if (key === 'q') {
        query = value;
      } else {
        this.helper.addFacetRefinement(key, value);
      }
    });

    if (query) {
      this.$input.value = query;
      this.helper.setQuery(query).search();
    }
  }

  setFacet(key, value) {
    this.helper.clearRefinements();
    if (key && value) this.helper.addFacetRefinement(key, value);
    this.facetValue = value; // set for internal state
  }

  clearHits() {
    this.$main.innerHTML = '';
  }

  clearFacets() {
    this.$sidebar.innerHTML = '';
  }

  trackViewEvent(hits) {
    if (hits.length === 0 || !window.__algoliaInsightsInit) return;
    const objectIDs = hits.map((hit) => hit.objectID);
    window.aa('viewedObjectIDs', {
      index: ALGOLIA_INDEX,
      eventName: 'Search Results Viewed',
      objectIDs,
    });
  }

  onResultClick(event) {
    if (!window.__algoliaInsightsInit) return;

    const $link = event.target.closest('.search-hit__link');
    if (!$link) return;

    const objectID = $link.getAttribute('data-object-id');
    const position = parseInt($link.getAttribute('data-position'), 10);
    const queryID = $link.getAttribute('data-query-id');

    if (!queryID) return;

    window.aa('clickedObjectIDsAfterSearch', {
      index: ALGOLIA_INDEX,
      eventName: 'Search Result Clicked',
      queryID,
      objectIDs: [objectID],
      positions: [position],
    });
  }
}


registerModule('SearchPage', SearchPage);

// -------------------------- getHighlightHtml -------------------------- //

const getHrefMap = {
  page: (hit) => `/${hit.slug}`,
  podcastEpisode: (hit) => {
    return `/insights/podcasts/${slugify(hit.podcastTitle)}/${slugify(hit.title)}`;
  },
  newsroom: (hit) => `/newsroom/${slugify(hit.slug)}`,
  research: (hit) => `/insights/research/${slugify(hit.slug)}`,
  perspectives: (hit) => `/insights/perspectives/${slugify(hit.slug)}`,
  'case-studies': (hit) => `/insights/case-studies/${slugify(hit.slug)}`,
  videos: (hit) => `/insights/videos/${slugify(hit.slug)}`,
};

const FALLBACK_THUMBNAIL_SVG = `
  <svg class="search-hit__thumbnail search-hit__thumbnail--fallback" width="150" height="100">
    <use xlink:href="#search-hit-fallback-thumbnail" />
  </svg>
`;

/**
 * get search results item HTML for search result hit
 * @param {Object} hit
 * @param {number} position - 1-indexed position in results
 * @param {string} queryID - Algolia queryID for click analytics
 * @return {String}
 */
function getHitHtml(hit, position, queryID) {
  const getHref = getHrefMap[hit.type];
  const href = getHref ? getHref(hit) : `/${hit.slug}`;

  let eyebrow = getTitle(hit.type);
  if (hit.date) eyebrow += ' • ' + hit.date;

  return `
    <div class="search-hit">
      <a class="search-hit__link" href="${href}" data-object-id="${hit.objectID}" data-position="${position}" data-query-id="${queryID}">
        ${getThumbnailHtml(hit)}
        <div class="search-hit__main">
          <p class="search-hit__eyebrow type-eyebrow">${eyebrow}</p>
          <h3 class="type-subhead">${hit.title}</h3>
          ${getHighlightHtml(hit)}
        </div>
      </a>
    </div>
  `;
}

/**
 * get thumbnail HTML
 * @param {Object} hit
 * @return {String}
 */
function getThumbnailHtml(hit) {
  if (!hit.image) return FALLBACK_THUMBNAIL_SVG;

  let imageSrc = hit.image;
  if (imageSrc.includes('images.ctfassets.net')) {
    imageSrc += '?w=400&q=75&fm=jpg&fl=progressive';
  }
  return `<img class="search-hit__thumbnail search-hit__thumbnail--img" src="${imageSrc}" />`;
}

const $proxyElem = document.createElement('div');

/**
 * get highlight HTML
 * @param {Object} hit
 * @return {String}
 */
function getHighlightHtml(hit) {
  if (!hit._highlightResult) return '';

  const highlights = [
    hit._highlightResult.body1,
    hit._highlightResult.body2,
    hit._highlightResult.body3,
    hit._highlightResult.body4,
    hit._highlightResult.subtitle,
  ].filter(Boolean);

  for (const highlight of highlights) {
    if (highlight.matchLevel !== 'full') continue;

    $proxyElem.innerHTML = highlight.value;
    const $em = $proxyElem.querySelector('em');
    if (!$em) continue;

    // compile string before and after the <em>
    const {textContent} = $proxyElem;
    const markText = $em.textContent;
    const markIndex = textContent.indexOf(markText);
    if (markIndex === -1) continue;

    const firstIndex = Math.max(markIndex - 40, 0);
    const lastIndex = Math.min(markIndex + markText.length + 40, textContent.length);

    let highlightHtml = textContent.slice(firstIndex, lastIndex);
    highlightHtml = highlightHtml.replace(markText, `<mark>${markText}</mark>`);
    if (firstIndex !== 0) highlightHtml = `...${highlightHtml}`;
    if (lastIndex !== highlight.value.length) highlightHtml = `${highlightHtml}...`;
    return `<div class="search-hit__highlight">${highlightHtml}</div>`;
  }

  return '';
}
